import {useCallback, useEffect, useState} from 'react';

type UseUnsavedChangesGuardProps = {
    isOpen: boolean;
    formState: {isDirty: boolean};
    preferredPlacesModified: boolean;
    onClose: () => void;
};

type UseUnsavedChangesGuardReturn = {
    showCloseConfirmAlert: boolean;
    setShowCloseConfirmAlert: (show: boolean) => void;
    allowDismiss: boolean;
    setAllowDismiss: (allow: boolean) => void;
    userInteracted: boolean;
    setUserInteracted: (interacted: boolean) => void;
    hasUnsavedChanges: () => boolean;
    handleCanDismiss: () => Promise<boolean>;
    handleDidDismiss: () => void;
    confirmClose: () => void;
    handleCloseButtonClick: () => Promise<void>;
};

export const useUnsavedChangesGuard = ({isOpen, formState, preferredPlacesModified, onClose}: UseUnsavedChangesGuardProps): UseUnsavedChangesGuardReturn => {
    const [showCloseConfirmAlert, setShowCloseConfirmAlert] = useState(false);
    const [allowDismiss, setAllowDismiss] = useState(false);
    const [userInteracted, setUserInteracted] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setAllowDismiss(false);
            setUserInteracted(false);
            setShowCloseConfirmAlert(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && formState.isDirty) {
            const timer = setTimeout(() => {
                if (formState.isDirty) {
                    setUserInteracted(true);
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isOpen, formState.isDirty]);

    const hasUnsavedChanges = useCallback(() => {
        return (userInteracted && formState.isDirty) || preferredPlacesModified;
    }, [userInteracted, formState.isDirty, preferredPlacesModified]);

    const handleCanDismiss = useCallback(async () => {
        if (allowDismiss) {
            return true;
        }

        if (hasUnsavedChanges()) {
            setShowCloseConfirmAlert(true);
            return false;
        }

        return true;
    }, [allowDismiss, hasUnsavedChanges]);

    const handleDidDismiss = useCallback(() => {
        if (!showCloseConfirmAlert || allowDismiss) {
            onClose();
        }
    }, [showCloseConfirmAlert, allowDismiss, onClose]);

    const confirmClose = useCallback(() => {
        setShowCloseConfirmAlert(false);
        setAllowDismiss(true);
        onClose();
    }, [onClose]);

    const handleCloseButtonClick = useCallback(async () => {
        const canClose = await handleCanDismiss();
        if (canClose) {
            onClose();
        }
    }, [handleCanDismiss, onClose]);

    return {
        showCloseConfirmAlert,
        setShowCloseConfirmAlert,
        allowDismiss,
        setAllowDismiss,
        userInteracted,
        setUserInteracted,
        hasUnsavedChanges,
        handleCanDismiss,
        handleDidDismiss,
        confirmClose,
        handleCloseButtonClick,
    };
};
